import { useEffect, useMemo, useState } from 'react'

import { useGetUserInfo } from '@/modules/Auth'
import { useGetHubs } from '../api/useGetHubs'
import { useWorkstationStore } from '../store'
import { HubWorkstation } from '@/types/hub'

export const useWorkstationSelect = () => {
    const [selectedValue, setSelectedValue] = useState('')
    const { workstation, setWorkstation, setRoutes, setReassignedRoutes } = useWorkstationStore()
    const { data: hubs, isLoading } = useGetHubs()
    const { data: userInfo } = useGetUserInfo()

    const selectedHub = useMemo(
        () => (hubs && userInfo ? hubs?.find((hub) => hub.code === userInfo?.lastSessionDetails.busHubCode) : null),
        [hubs, userInfo]
    )

    const allWorkstations = useMemo(() => (hubs ? hubs.flatMap((item) => item.workstations) : []), [hubs])

    const options = useMemo(() => {
        if (isLoading || !hubs) {
            return []
        }

        return hubs.map((hub) => ({
            label: hub.hubTitle,
            value: hub.code,
            options: hub.workstations.map((workstation) => ({
                label: workstation.workstationNumber,
                value: workstation.workstationNumber,
            })),
        }))
    }, [isLoading, hubs])

    const selectWorkstation = (workstation: HubWorkstation): void => {
        const { routes, routeReassignments, ...workstationData } = workstation

        setWorkstation(workstationData)
        setRoutes(routes)
        setReassignedRoutes(routeReassignments)
    }

    const handleWorkstationChange = (workstationNumber: string): void => {
        const workstation = allWorkstations.find((item) => item.workstationNumber === workstationNumber)

        if (workstation) {
            setSelectedValue(workstationNumber)
            selectWorkstation(workstation)
        }
    }

    const initWorkstation = () => {
        const selectedHub = hubs?.find((hub) => hub.code === userInfo?.lastSessionDetails.busHubCode)

        if (!selectedHub) {
            return
        }

        const defaultWorkstation = selectedHub.workstations.at(0)

        if (defaultWorkstation) {
            handleWorkstationChange(defaultWorkstation.workstationNumber)
        }
    }

    useEffect(() => {
        if (!selectedHub) {
            return
        }

        if (!workstation) {
            initWorkstation()
            return
        }

        handleWorkstationChange(workstation.workstationNumber)
    }, [hubs, selectedHub])

    useEffect(() => {
        if (!workstation) {
            return
        }

        setSelectedValue(workstation.workstationNumber)
    }, [workstation])

    return {
        options,
        selectedValue,
        handleWorkstationChange,
    }
}
